import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import type { Pdf as Interface, FileMetadata } from '../../design/components';
import { isFileMetadata } from '../shared/image';
import { isValidPdfSource, downloadPdf, type PdfDocProxy } from '../shared/pdf';

export const Pdf: React.FC<Interface> = ({
  name,
  description,
  state,
  placeholder = 'No PDF selected',
  hidden,
  inline,
  readonly: _readonly,
  disabled,
  required,
  error,
}) => {
  const [value, setValue] = state || ['' as unknown as FileMetadata, () => {}];
  const valueRef = useRef(value);
  valueRef.current = value;
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isValid = isValidPdfSource(value);

  const displayUrl = useMemo(() => isValid ? (value as FileMetadata).url : '', [value, isValid]);

  useEffect(() => {
    return () => { if (displayUrl.startsWith('blob:')) URL.revokeObjectURL(displayUrl); };
  }, [displayUrl]);

  const handleDocumentLoadSuccess = (pdf: PdfDocProxy) => {
    setNumPages(pdf.numPages);
    const current = valueRef.current;
    if (isFileMetadata(current) && current.file) {
      (async () => {
        const text: string[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          text.push(content.items.map(item => ('str' in item ? item.str : '')).join(' '));
        }
        const searchable = text.some(t => t.length > 0);
        setValue({ ...valueRef.current as FileMetadata, pages: pdf.numPages, searchable, text });
      })().catch(() => {});
    }
  };

  const handleDownload = () => {
    if (isFileMetadata(value)) downloadPdf(value.url, value.name);
  };

  const processFile = (file: File) => {
    if (file.type === 'application/pdf') {
      setValue({
        url: URL.createObjectURL(file),
        name: file.name, type: file.type, size: file.size,
        modified: new Date(file.lastModified).toISOString(),
        pages: 0, file,
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth - 2);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [isValid]);

  const controlsHeight = 40;
  const previewHeight = 300;
  const containerHeight = previewHeight + controlsHeight;

  if (inline) {
    return value ? (
      <span style={{ display: hidden ? 'none' : 'inline' }}>PDF</span>
    ) : null;
  }

  const canUpload = !_readonly && !disabled;

  return (
    <div style={{ marginBottom: '1rem', display: hidden ? 'none' : undefined }}>
      {name && (
        <label htmlFor={name} style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
          {name}
          {required && <span style={{ color: 'red' }}> *</span>}
        </label>
      )}

      {/* PDF Preview */}
      {isValid && (
        <div
          ref={containerRef}
          style={{
            width: '100%',
            border: '1px solid #ccc',
            borderRadius: '4px',
            backgroundColor: '#f5f5f5',
            overflow: expanded ? 'auto' : 'hidden',
            height: expanded ? undefined : containerHeight,
            maxHeight: expanded ? '80vh' : undefined,
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {/* Controls */}
          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '8px 0',
              borderBottom: '1px solid #ccc',
              backgroundColor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(8px)',
            }}
          >
            {numPages && numPages > 1 && (
              <>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px' }}
                >
                  &#9664;
                </button>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {currentPage} / {numPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                  disabled={currentPage >= numPages}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px' }}
                >
                  &#9654;
                </button>
              </>
            )}

            <button
              onClick={() => setExpanded(!expanded)}
              title={expanded ? 'Shrink' : 'Expand'}
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px' }}
            >
              {expanded ? '\u229F' : '\u229E'}
            </button>

            {isFileMetadata(value) && (
              <a
                href={value.url}
                target="_blank"
                rel="noopener noreferrer"
                title="Open in new tab"
                style={{ padding: '4px', color: 'inherit' }}
              >
                {'\u2197'}
              </a>
            )}

            <button
              onClick={handleDownload}
              title="Download"
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px' }}
            >
              {'\u2B07'}
            </button>

            {canUpload && (
              <button
                onClick={() => setValue('' as unknown as FileMetadata)}
                title="Remove"
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px' }}
              >
                {'\u{1F5D1}'}
              </button>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Document
              file={displayUrl}
              onLoadSuccess={handleDocumentLoadSuccess}
              loading={<div style={{ padding: '16px', fontSize: '14px', color: '#666' }}>Loading...</div>}
              error={<div style={{ padding: '16px', fontSize: '14px', color: 'red' }}>Failed to load PDF</div>}
              noData={<div style={{ padding: '16px', fontSize: '14px', color: '#666' }}>No PDF</div>}
            >
              <Page
                pageNumber={currentPage}
                width={expanded ? containerWidth : undefined}
                height={expanded ? undefined : previewHeight}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading={<div style={{ padding: '16px', fontSize: '14px', color: '#666' }}>Loading page...</div>}
                error={<div style={{ padding: '16px', fontSize: '14px', color: 'red' }}>Failed to load page</div>}
              />
            </Document>
          </div>
        </div>
      )}

      {/* Upload zone - empty state */}
      {!isValid && canUpload && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '96px',
            borderRadius: '4px',
            border: dragOver ? '1px dashed #1976d2' : error ? '1px dashed red' : '1px dashed #ccc',
            backgroundColor: dragOver ? '#e3f2fd' : '#f5f5f5',
            color: '#666',
            cursor: 'pointer',
            transition: 'border-color 0.2s, background-color 0.2s',
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <span style={{ fontSize: '24px', marginBottom: '4px' }}>{'\u2B06'}</span>
          <span style={{ fontSize: '14px' }}>Click or drag to upload PDF</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      )}

      {/* Static empty state for readonly/disabled */}
      {!isValid && !canUpload && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '96px',
            borderRadius: '4px',
            border: error ? '1px solid red' : '1px solid #ccc',
            backgroundColor: '#f5f5f5',
            color: '#666',
          }}
        >
          <span style={{ marginRight: '8px' }}>{'\uD83D\uDCC4'}</span>
          <span>{placeholder}</span>
        </div>
      )}

      {(error || description) && (
        <small style={{ color: error ? 'red' : '#666', display: 'block', marginTop: '4px' }}>
          {error || description}
        </small>
      )}
    </div>
  );
};
